import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  listCatalogDrafts, confirmClass, setCanonicalFile, mergeClass, splitClass, unsplitClass,
  listSources, addSourceLink, addSourceUpload, syncSource, deleteSource,
  setOfferPricing, listConfirmedClasses,
  generateIndexDoc, getReconcile, runReconcile,
  updateCatalogItem, enrichPending, enrichCatalogItem,
  listSyncs, triggerSync, getTrainingCapabilities,
} from './api';

const LEVELS = [['', '(none)'], ['introductory', 'Introductory'], ['intermediate', 'Intermediate'], ['advanced', 'Advanced']];
const KINDS = [['course', 'Course'], ['bundle', 'Bundle'], ['pathway', 'Pathway'], ['exam', 'Exam']];
const ENRICH_FILTERS = [['all', 'All'], ['needs_review', 'Needs review'], ['pending', 'Pending'], ['enriched', 'Enriched'], ['failed', 'Failed']];
const ENRICH_LABEL = { pending: 'Pending', enriched: 'Enriched', needs_review: 'Needs review', failed: 'Failed' };

function EnrichBadge({ status }) {
  const cls = {
    pending: 'bg-gray-100 text-gray-600', enriched: 'bg-blue-50 text-blue-600',
    needs_review: 'bg-orange-50 text-orange-600', failed: 'bg-orange-100 text-orange-700',
  }[status] || 'bg-gray-100 text-gray-600';
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{ENRICH_LABEL[status] || status}</span>;
}

function ConfidencePill({ c }) {
  if (!c) return null;
  const cls = { high: 'text-blue-600', medium: 'text-gray-500', low: 'text-orange-600' }[c] || 'text-gray-500';
  return <span className={`text-[10px] ${cls}`}>confidence: {c}</span>;
}

const dollarsToCents = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};
const centsToDollars = (c) => (c || c === 0 ? (c / 100).toFixed(2) : '');

const ORIGINS = [['legacy', 'Legacy'], ['jpro_created', 'JPRO-created']];
const BUILD = [['deployed', 'Deployed'], ['in_progress', 'In progress']];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1]); // strip data: prefix
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function ContentSources() {
  const qc = useQueryClient();
  const [year, setYear] = useState('');
  const [link, setLink] = useState('');
  const { data } = useQuery({ queryKey: ['content-sources'], queryFn: listSources, retry: false });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['content-sources'] });
    qc.invalidateQueries({ queryKey: ['catalog-drafts'] });
  };
  const addLink = useMutation({
    mutationFn: () => addSourceLink(parseInt(year, 10), link),
    onSuccess: () => { toast.success('Source added + imported'); setLink(''); setYear(''); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });
  const upload = useMutation({
    mutationFn: async (file) => addSourceUpload(parseInt(year, 10), await fileToBase64(file), file.name),
    onSuccess: () => { toast.success('Uploaded + imported'); setYear(''); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });
  const sync = useMutation({ mutationFn: (id) => syncSource(id), onSuccess: () => { toast.success('Synced'); invalidate(); } });
  const del = useMutation({ mutationFn: (id) => deleteSource(id), onSuccess: invalidate });

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-6">
      <h3 className="text-sm font-semibold mb-1">Content Sources (per year)</h3>
      <p className="text-xs text-gray-500 mb-3">Register each year's index doc by SharePoint link (auto-tracked on the schedule) or upload the .docx.</p>

      <table className="w-full text-xs mb-3">
        <tbody>
          {data?.sources?.map((s) => (
            <tr key={s.id} className="border-b border-gray-100 dark:border-gray-800">
              <td className="py-2 font-semibold">{s.year}</td>
              <td className="py-2">{s.source_type === 'link' ? `🔗 ${s.share_url}` : `⬆ ${s.uploaded_filename || 'upload'}`}</td>
              <td className="py-2 text-gray-500">{s.last_status}{s.last_synced_at ? ` · ${new Date(s.last_synced_at).toLocaleString()}` : ''}</td>
              <td className="py-2 text-right">
                {s.source_type === 'link' && <button onClick={() => sync.mutate(s.id)} className="text-blue-600 mr-3">sync</button>}
                <button onClick={() => del.mutate(s.id)} className="text-red-500">remove</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex flex-wrap items-center gap-2">
        <input type="number" value={year} placeholder="Year" onChange={(e) => setYear(e.target.value)}
          className="w-20 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-transparent" />
        <input value={link} placeholder="SharePoint index-doc link" onChange={(e) => setLink(e.target.value)}
          className="flex-1 min-w-[220px] text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-transparent" />
        <button disabled={!year || !link || addLink.isPending} onClick={() => addLink.mutate()}
          className="text-xs font-bold px-3 py-1.5 rounded-lg bg-orange-500 text-white disabled:opacity-40">Add link</button>
        <label className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 cursor-pointer">
          Upload .docx
          <input type="file" accept=".docx" className="hidden"
            onChange={(e) => { if (e.target.files[0] && year) upload.mutate(e.target.files[0]); else toast.error('Enter a year first'); }} />
        </label>
      </div>
    </div>
  );
}

function ClassCard({ cls, siblings }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    year: cls.year || '',
    lms_deployed: cls.lms_deployed || false,
    origin: cls.origin || 'legacy',
    build_status: cls.build_status || 'deployed',
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['catalog-drafts'] });

  const confirm = useMutation({
    mutationFn: () => confirmClass(cls.id, { ...form, year: parseInt(form.year, 10) || null }),
    onSuccess: () => { toast.success('Confirmed'); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Not permitted'),
  });
  const canonical = useMutation({
    mutationFn: (fileId) => setCanonicalFile(cls.id, fileId),
    onSuccess: invalidate,
  });
  const merge = useMutation({
    mutationFn: (intoId) => mergeClass(cls.id, intoId),
    onSuccess: () => { toast.success('Merged'); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });
  const mergeTargets = (siblings || []).filter((s) => s.id !== cls.id);

  const [splitPicks, setSplitPicks] = useState([]);
  const split = useMutation({
    mutationFn: () => splitClass(cls.id, splitPicks),
    onSuccess: () => { toast.success('Split into per-script classes'); setSplitPicks([]); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });
  const unsplit = useMutation({
    mutationFn: () => unsplitClass(cls.id),
    onSuccess: () => { toast.success('Merged back into one class'); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });
  const scripts = cls.files.filter((f) => f.kind === 'script');
  const togglePick = (id) => setSplitPicks((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const [offer, setOffer] = useState({
    offered: cls.offered_to_clients || false,
    seat: centsToDollars(cls.seat_price_cents),
    dev: centsToDollars(cls.dev_setup_fee_cents),
  });
  const saveOffer = useMutation({
    mutationFn: () => {
      // Only send price fields the director actually entered, so a blank input
      // never NULLs a previously-set price. `offered` is always sent.
      const body = { offered: offer.offered };
      const seat = dollarsToCents(offer.seat);
      if (seat != null) body.seat_price_cents = seat;
      const dev = dollarsToCents(offer.dev);
      if (dev != null) body.dev_setup_fee_cents = dev;
      return setOfferPricing(cls.id, body);
    },
    onSuccess: () => { toast.success('Offer + pricing saved'); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Not permitted'),
  });

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{cls.title}</h4>
        <span className="text-[10px] uppercase tracking-wide text-gray-400">{cls.kind}</span>
      </div>

      <div className="mt-2 space-y-1">
        {cls.files.map((f) => (
          <div key={f.id} className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
            <span>
              <span className="text-[9px] uppercase text-gray-400 mr-1">{f.kind}</span>
              {f.filename} {f.format && <span className="text-gray-400">.{f.format}</span>}
            </span>
            {f.kind === 'script' && (
              <button
                onClick={() => canonical.mutate(f.id)}
                className={`text-[11px] px-2 py-0.5 rounded ${f.is_canonical_for_text ? 'bg-green-100 text-green-700' : 'border border-gray-300 text-gray-500'}`}
              >
                {f.is_canonical_for_text ? '★ canonical' : 'set canonical'}
              </button>
            )}
          </div>
        ))}
        {cls.files.length === 0 && <div className="text-xs text-gray-400">no files linked</div>}
      </div>

      {cls.is_split && (
        <div className="mt-3 flex items-center justify-between rounded-lg bg-blue-50 dark:bg-blue-900/20 px-3 py-2">
          <span className="text-xs text-blue-700 dark:text-blue-300">Split off from a shared folder.</span>
          <button onClick={() => unsplit.mutate()} disabled={unsplit.isPending}
            className="text-[11px] font-semibold px-2 py-0.5 rounded border border-blue-400 text-blue-700 dark:text-blue-300 disabled:opacity-40">
            Un-split (merge folder back)
          </button>
        </div>
      )}

      {cls.split_candidate && (
        <div className="mt-3 rounded-lg border border-blue-200 dark:border-blue-800 p-3">
          <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1">
            Multiple scripts in this folder. Split into a class per script?
          </div>
          <div className="space-y-1">
            {scripts.map((f) => (
              <label key={f.id} className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
                <input type="checkbox" checked={splitPicks.includes(f.id)} onChange={() => togglePick(f.id)} />
                {f.filename}
              </label>
            ))}
          </div>
          <button onClick={() => split.mutate()} disabled={split.isPending || splitPicks.length === 0}
            className="mt-2 text-[11px] font-semibold px-3 py-1 rounded-lg bg-blue-600 text-white disabled:opacity-40">
            Split selected into own classes
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <input
          type="number" value={form.year} placeholder="Year"
          onChange={(e) => setForm({ ...form, year: e.target.value })}
          className="w-20 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-transparent"
        />
        <select value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })}
          className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800">
          {ORIGINS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={form.build_status} onChange={(e) => setForm({ ...form, build_status: e.target.value })}
          className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800">
          {BUILD.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={form.lms_deployed}
            onChange={(e) => setForm({ ...form, lms_deployed: e.target.checked })} />
          Deployed to LMS
        </label>
        {mergeTargets.length > 0 && (
          <select defaultValue="" onChange={(e) => e.target.value && merge.mutate(e.target.value)}
            className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:[&>option]:bg-gray-800">
            <option value="">merge into…</option>
            {mergeTargets.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
        )}
        <button onClick={() => confirm.mutate()} disabled={confirm.isPending}
          className="ml-auto text-xs font-bold px-3 py-1.5 rounded-lg bg-orange-500 text-white disabled:opacity-40">
          Confirm
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
        <label className="text-xs flex items-center gap-1">
          <input type="checkbox" checked={offer.offered}
            onChange={(e) => setOffer({ ...offer, offered: e.target.checked })} />
          Offer to clients
        </label>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-gray-400">$</span>
          <input type="number" step="0.01" min="0" value={offer.seat} placeholder="Seat price"
            onChange={(e) => setOffer({ ...offer, seat: e.target.value })}
            className="w-24 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-transparent" />
          <span className="text-gray-400">/ seat</span>
        </div>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-gray-400">$</span>
          <input type="number" step="0.01" min="0" value={offer.dev} placeholder="Dev fee (optional)"
            onChange={(e) => setOffer({ ...offer, dev: e.target.value })}
            className="w-28 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-transparent" />
        </div>
        <button onClick={() => saveOffer.mutate()} disabled={saveOffer.isPending}
          className="ml-auto text-xs font-bold px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-40">
          Save offer
        </button>
      </div>
    </div>
  );
}

function OfferedPricing() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['confirmed-classes'], queryFn: listConfirmedClasses, retry: false,
  });
  const [edits, setEdits] = useState({}); // id -> {seat, dev, lms}
  const save = useMutation({
    mutationFn: ({ id, offered, seat, dev, lms }) => {
      const body = { offered };
      const s = dollarsToCents(seat); if (s != null) body.seat_price_cents = s;
      const d = dollarsToCents(dev); if (d != null) body.dev_setup_fee_cents = d;
      // Always sent, so clearing the field drops the per-class link and the
      // client falls back to the global LMS portal URL.
      body.lms_url = (lms || '').trim();
      return setOfferPricing(id, body);
    },
    onSuccess: (_r, vars) => {
      toast.success('Saved');
      setEdits((cur) => { const n = { ...cur }; delete n[vars.id]; return n; }); // drop stale local edit
      qc.invalidateQueries({ queryKey: ['confirmed-classes'] });
    },
    onError: (e) => toast.error(e?.message || 'Not permitted'),
  });

  if (isLoading) return <div className="text-xs text-gray-500 mb-6">Loading confirmed classes…</div>;
  if (isError) return <div className="text-xs text-red-500 mb-6">Could not load confirmed classes.</div>;
  const classes = data?.classes || [];
  if (classes.length === 0) return null;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-6">
      <h3 className="text-sm font-semibold mb-1">Confirmed classes &amp; pricing</h3>
      <p className="text-xs text-gray-500 mb-3">
        Offer or stop offering a confirmed class and set its seat price / dev fee. Only offered, priced classes are
        buyable by clients. Leave the LMS link blank to send buyers to the portal URL set under Client Portal.
      </p>
      {classes.map((c) => {
        const e = edits[c.id] || {
          seat: centsToDollars(c.seat_price_cents),
          dev: centsToDollars(c.dev_setup_fee_cents),
          lms: c.lms_url || '',
        };
        const set = (patch) => setEdits((cur) => ({ ...cur, [c.id]: { ...e, ...patch } }));
        return (
          <div key={c.id} className="flex flex-wrap items-center gap-2 text-xs py-2 border-b border-gray-100 dark:border-gray-800">
            <span className="font-semibold flex-1 min-w-[140px]">
              {c.title}
              {c.offered_to_clients
                ? <span className="ml-2 text-[10px] text-blue-600">offered</span>
                : <span className="ml-2 text-[10px] text-gray-400">not offered</span>}
            </span>
            <span className="text-gray-400">$</span>
            <input type="number" step="0.01" min="0" value={e.seat} aria-label={`Seat price for ${c.title}`}
              onChange={(ev) => set({ seat: ev.target.value })}
              className="w-24 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-transparent" />
            <span className="text-gray-400">/ seat · $</span>
            <input type="number" step="0.01" min="0" value={e.dev} aria-label={`Dev fee for ${c.title}`}
              onChange={(ev) => set({ dev: ev.target.value })}
              className="w-24 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-transparent" />
            <input type="url" value={e.lms} aria-label={`LMS link for ${c.title}`} placeholder="LMS link (optional)"
              onChange={(ev) => set({ lms: ev.target.value })}
              className="w-56 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-transparent" />
            <button onClick={() => save.mutate({ id: c.id, offered: true, seat: e.seat, dev: e.dev, lms: e.lms })}
              className="px-3 py-1 rounded-lg bg-blue-600 text-white">{c.offered_to_clients ? 'Save' : 'Offer'}</button>
            {c.offered_to_clients && (
              <button onClick={() => save.mutate({ id: c.id, offered: false, seat: e.seat, dev: e.dev, lms: e.lms })}
                className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-orange-600">Stop offering</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CatalogHealth() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [force, setForce] = useState(false);
  const { data: rec, refetch } = useQuery({ queryKey: ['catalog-reconcile'], queryFn: getReconcile, retry: false });

  const generate = useMutation({
    mutationFn: () => generateIndexDoc(parseInt(year, 10), force),
    onSuccess: (r) => {
      const g = r.generated || {};
      toast.success(`Index doc generated: ${g.classes ?? 0} classes${g.linkless ? `, ${g.linkless} without a link` : ''}`);
    },
    onError: (e) => toast.error(e?.message || 'Generate failed (try Replace existing if a source exists)'),
  });

  const reconcile = useMutation({
    mutationFn: runReconcile,
    onSuccess: () => { toast.success('Reconcile queued'); setTimeout(refetch, 2000); },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });

  const run = rec?.last_run;
  const drift = rec?.drift_classes || [];
  const unlinked = rec?.unlinked || [];

  return (
    <div className="mb-6 border border-gray-200 rounded-lg p-4">
      <div className="text-sm font-semibold mb-3">Catalog health</div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs text-gray-500">Generate index doc for</span>
        <input value={year} onChange={(e) => setYear(e.target.value)}
          className="w-20 border border-gray-300 rounded px-2 py-1 text-xs" />
        <label className="text-xs text-gray-500 flex items-center gap-1">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} /> Replace existing source
        </label>
        <button onClick={() => generate.mutate()} disabled={generate.isPending}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-orange-500 text-white disabled:opacity-50">
          {generate.isPending ? 'Generating…' : 'Generate'}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-2">
        <button onClick={() => reconcile.mutate()} disabled={reconcile.isPending}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-blue-500 text-blue-500 disabled:opacity-50">
          {reconcile.isPending ? 'Queuing…' : '↻ Run reconcile'}
        </button>
        {run && (
          <span className="text-xs text-gray-500">
            {run.skipped
              ? `Last run skipped: ${run.skipped}`
              : `Last: ${run.checked} checked · ${run.drift} drift · ${run.unlinked_count} unlinked`}
            {run.ran_at ? ` · ${new Date(run.ran_at).toLocaleString()}` : ''}
          </span>
        )}
      </div>

      {drift.length > 0 && (
        <div className="mt-2">
          <div className="text-xs font-semibold text-orange-600 mb-1">Drift — canonical folder missing ({drift.length})</div>
          {drift.map((d) => (
            <div key={d.id} className="text-xs text-gray-600">{d.program} · {d.title}</div>
          ))}
        </div>
      )}

      {unlinked.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-semibold text-blue-600 mb-1">Unlinked folders — no catalog class ({unlinked.length})</div>
          {unlinked.map((u) => (
            <div key={u.id} className="text-xs text-gray-600">
              {u.web_url ? <a href={u.web_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{u.path}</a> : u.path}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const csv = (arr) => (Array.isArray(arr) ? arr.join(', ') : '');
const toArr = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);

// Module scope on purpose, and it has to stay here. Declared inside EnrichRow this
// got a fresh function identity on every render, which makes React treat it as a
// different component type and remount everything under it - so each keystroke
// destroyed the input it was typing into and focus jumped out of the field. Same
// reason the class string is a constant rather than a per-render local.
const FIELD_INPUT = 'w-full border border-gray-300 rounded px-2 py-1 text-xs mt-0.5';

function Field({ label, source, children }) {
  return (
    <label className="block text-[11px] text-gray-500 mb-2">
      <span className="flex items-center gap-1">{label}<span className="text-[9px] text-gray-400">({source})</span></span>
      {children}
    </label>
  );
}

function EnrichRow({ item }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const seed = () => ({
    title: item.title || '', kind: item.kind || 'course', program: item.program || '',
    summary: item.summary || '', level: item.level || '', delivery_type: item.delivery_type || '',
    duration_minutes: item.duration_minutes ?? '', prerequisites: item.prerequisites || '',
    audiences: csv(item.audiences), tags: csv(item.tags),
  });
  const [form, setForm] = useState(seed);
  const flags = item.enrichment_flags || {};
  const overrides = item.human_overrides || [];
  const invalidate = () => qc.invalidateQueries({ queryKey: ['catalog-drafts'] });

  const save = useMutation({
    // Send ONLY changed fields so human_overrides stays precise.
    mutationFn: () => {
      const orig = seed();
      const payload = {};
      ['title', 'kind', 'program', 'summary', 'level', 'delivery_type', 'prerequisites'].forEach((k) => {
        if (form[k] !== orig[k]) payload[k] = form[k];
      });
      if (String(form.duration_minutes) !== String(orig.duration_minutes)) {
        payload.duration_minutes = form.duration_minutes === '' ? null : parseInt(form.duration_minutes, 10);
      }
      if (form.audiences !== orig.audiences) payload.audiences = toArr(form.audiences);
      if (form.tags !== orig.tags) payload.tags = toArr(form.tags);
      return updateCatalogItem(item.id, payload);
    },
    onSuccess: () => { toast.success('Saved'); setOpen(false); invalidate(); },
    onError: (e) => toast.error(e?.message || 'Save failed'),
  });

  const reEnrich = useMutation({
    mutationFn: () => enrichCatalogItem(item.id),
    onSuccess: () => { toast.success('Re-enrich queued'); setTimeout(invalidate, 2500); },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });

  const src = (field) => (overrides.includes(field) ? 'Edited' : (item.enrichment_status === 'pending' ? 'From folder' : 'AI'));

  return (
    <div className="border border-gray-200 rounded-lg px-3 py-2 mb-2">
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate">{item.title}</div>
          <div className="text-[10px] text-gray-500">{item.program || 'Unassigned'} · {item.kind}</div>
        </div>
        <EnrichBadge status={item.enrichment_status} />
        <ConfidencePill c={item.enrichment_confidence} />
        <button onClick={() => setOpen((o) => !o)} className="text-[11px] text-blue-600 hover:underline">{open ? 'Close' : 'Edit'}</button>
        <button onClick={() => reEnrich.mutate()} disabled={reEnrich.isPending}
          className="text-[11px] text-gray-500 hover:text-blue-600 disabled:opacity-50">Re-enrich</button>
      </div>

      {open && (
        <div className="mt-3">
          {Object.keys(flags).length > 0 && (
            <div className="mb-3 rounded-lg border border-orange-200 bg-orange-50 p-2">
              <div className="text-[11px] font-semibold text-orange-700 mb-1">The AI reading diverges from the folder</div>
              {Object.entries(flags).map(([f, info]) => (
                <div key={f} className="text-[11px] text-gray-700 flex items-center gap-2 flex-wrap">
                  <b>{f}</b>: folder says <code>{info.path}</code>, content suggests <code>{info.ai}</code>
                  {info.note ? <span className="text-gray-500">- {info.note}</span> : null}
                  <button onClick={() => setForm((c) => ({ ...c, [f]: info.ai }))}
                    className="text-[10px] font-semibold px-2 py-0.5 rounded border border-blue-500 text-blue-600">Use content value</button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-x-3">
            <Field label="Title" source={src('title')}><input className={FIELD_INPUT} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
            <Field label="Program" source={src('program')}><input className={FIELD_INPUT} value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} /></Field>
            <Field label="Kind" source={src('kind')}>
              <select className={FIELD_INPUT} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Level" source={src('level')}>
              <select className={FIELD_INPUT} value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>
                {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Duration (min)" source={src('duration_minutes')}><input type="number" className={FIELD_INPUT} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} /></Field>
            <Field label="Delivery" source={src('delivery_type')}><input className={FIELD_INPUT} value={form.delivery_type} onChange={(e) => setForm({ ...form, delivery_type: e.target.value })} /></Field>
          </div>
          <Field label="Summary" source={src('summary')}><textarea rows={2} className={FIELD_INPUT} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-x-3">
            <Field label="Audiences (comma-separated)" source={src('audiences')}><input className={FIELD_INPUT} value={form.audiences} onChange={(e) => setForm({ ...form, audiences: e.target.value })} /></Field>
            <Field label="Tags (comma-separated)" source={src('tags')}><input className={FIELD_INPUT} value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></Field>
          </div>
          <Field label="Prerequisites" source={src('prerequisites')}><input className={FIELD_INPUT} value={form.prerequisites} onChange={(e) => setForm({ ...form, prerequisites: e.target.value })} /></Field>
          {Array.isArray(item.objectives) && item.objectives.length > 0 && (
            <div className="text-[11px] text-gray-500 mb-2">Objectives ({item.objectives.length}): {item.objectives.map((o) => o.text).filter(Boolean).join('; ')}</div>
          )}
          <div className="flex justify-end">
            <button onClick={() => save.mutate()} disabled={save.isPending}
              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-blue-600 text-white disabled:opacity-50">
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function EnrichmentReview() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('needs_review');
  const { data } = useQuery({ queryKey: ['catalog-drafts'], queryFn: listCatalogDrafts, retry: false });
  const all = (data?.groups || []).flatMap((g) => g.classes || []);
  const counts = all.reduce((m, i) => { m[i.enrichment_status] = (m[i.enrichment_status] || 0) + 1; return m; }, {});
  const shown = filter === 'all' ? all : all.filter((i) => i.enrichment_status === filter);

  const enrich = useMutation({
    mutationFn: () => enrichPending(),
    onSuccess: (r) => { toast.success(`Enrichment queued (${r.pending ?? 0} pending)`); setTimeout(() => qc.invalidateQueries({ queryKey: ['catalog-drafts'] }), 3000); },
    onError: (e) => toast.error(e?.message || 'Failed'),
  });

  return (
    <div className="mb-6 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold">AI enrichment</div>
        <button onClick={() => enrich.mutate()} disabled={enrich.isPending || !(counts.pending > 0)}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-orange-500 text-white disabled:opacity-50">
          {enrich.isPending ? 'Queuing…' : `Enrich pending${counts.pending ? ` (${counts.pending})` : ''}`}
        </button>
      </div>
      <p className="text-[11px] text-gray-500 mb-3">
        The sync fills title, program and kind from the folder tree. Enrichment adds summary, level, audiences, tags and
        objectives with a fast model, and flags where its reading of the document disagrees with the folder. Any field you
        edit is kept and never overwritten by a later sync or re-enrichment.
      </p>
      <div className="flex flex-wrap gap-1 mb-3">
        {ENRICH_FILTERS.map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`text-[11px] px-2 py-1 rounded-full border ${filter === v ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 text-gray-600'}`}>
            {l}{v !== 'all' && counts[v] ? ` ${counts[v]}` : ''}
          </button>
        ))}
      </div>
      {shown.length === 0
        ? <div className="text-xs text-gray-500">No classes in this view.</div>
        : shown.slice(0, 200).map((i) => <EnrichRow key={i.id} item={i} />)}
      {shown.length > 200 && <div className="text-[11px] text-gray-400 mt-2">Showing first 200 of {shown.length}.</div>}
    </div>
  );
}

// Triggers the ZU library sync (admin only) and shows live run status. Lives here in
// the admin Catalog tab (moved from the content-library browse view).
function SyncNow() {
  const qc = useQueryClient();
  // Same endpoint as TrainingIntelligence/LmsAccess; sharing the key avoids a
  // second fetch and a second copy that can drift when settings change.
  const { data: caps } = useQuery({ queryKey: ['training-me'], queryFn: getTrainingCapabilities });
  const canSync = !!caps?.can_sync_library;
  // Optimistic lock between "trigger accepted" and "the poller can see the run".
  // Nothing server-side refuses a concurrent sync, so the disabled button is the
  // only thing stopping a double-click from enqueueing two walks.
  const [starting, setStarting] = useState(false);
  const startingRef = useRef(false);

  const { data: syncs, refetch } = useQuery({
    queryKey: ['training-syncs'],
    queryFn: listSyncs,
    retry: false,
    // Poll while a run is live, and while we are waiting for a just-triggered run to
    // appear - otherwise polling stops and the UI never learns the run started.
    refetchInterval: (query) => (
      query.state.data?.runs?.[0]?.status === 'running' || startingRef.current ? 4000 : false
    ),
  });

  const latest = syncs?.runs?.[0];
  const runStatus = latest?.status;
  const runId = latest?.id;
  const running = starting || runStatus === 'running';
  const sawRunning = useRef(false);
  const knownRunId = useRef(undefined);
  const triggeredAfterRunId = useRef(undefined);

  // Mirror committed state into the ref from an effect rather than during render,
  // so render stays pure; the poll interval is re-read on every fetch/re-render.
  useEffect(() => { startingRef.current = starting; }, [starting]);

  // Refresh the catalog when the run actually finishes. A real walk takes minutes and
  // the grouped classes only land at the end, so the running -> finished transition is
  // the signal; any fixed delay is either too early or too late. Keying on the run ID
  // as well as the status catches the cases a status string alone misses: a run that
  // starts and ends inside one poll gap, and a new run ending in the same status as
  // the previous one (failed -> failed).
  useEffect(() => {
    if (runId === undefined) return;
    const isNewRun = knownRunId.current !== undefined && runId !== knownRunId.current;
    const first = knownRunId.current === undefined;
    knownRunId.current = runId;

    if (runStatus === 'running') {
      sawRunning.current = true;
      setStarting(false);
      return;
    }
    // A brand-new run that is already finished never showed as running, so release
    // the lock on it too - otherwise a fast failure holds the button for 30s.
    const finishedRunWeTriggered = isNewRun && triggeredAfterRunId.current !== runId;
    if (finishedRunWeTriggered) setStarting(false);
    if (first || !(sawRunning.current || finishedRunWeTriggered)) return;

    sawRunning.current = false;
    qc.invalidateQueries({ queryKey: ['catalog-drafts'] });
    qc.invalidateQueries({ queryKey: ['confirmed-classes'] });
  }, [runId, runStatus, qc]);

  // Safety release, not an estimate of how long a sync takes: if the run never
  // surfaces at all (no worker), do not strand the button disabled.
  useEffect(() => {
    if (!starting) return undefined;
    const timer = setTimeout(() => setStarting(false), 30000);
    return () => clearTimeout(timer);
  }, [starting]);

  const start = useMutation({
    mutationFn: triggerSync,
    onMutate: () => { triggeredAfterRunId.current = knownRunId.current; setStarting(true); },
    onSuccess: () => { toast.success('Sync started'); refetch(); },
    // Surface what actually failed. This used to report "Admin only" for every
    // error, including network failures and a downed model_service.
    onError: (e) => { setStarting(false); toast.error(e?.message || 'Could not start the sync'); },
  });

  const sync = () => { if (!running) start.mutate(); };

  return (
    <div className="flex items-center gap-3 text-xs text-gray-500">
      {syncs && (
        <span>
          {syncs.item_count} classes{syncs.runs?.[0] && !running && ` · last ${syncs.runs[0].status}`}
        </span>
      )}
      {running && <span className="text-blue-500">Syncing the ZU library…</span>}
      {canSync && (
        <button
          onClick={sync}
          disabled={running}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-blue-500 text-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {running ? '⟳ Syncing…' : '↻ Sync now'}
        </button>
      )}
    </div>
  );
}

export default function CatalogCuration() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['catalog-drafts'], queryFn: listCatalogDrafts, retry: false });

  if (isError) return <div className="text-sm text-gray-500">Catalog curation is Director/admin only.</div>;
  if (isLoading) return <div className="text-sm text-gray-500">Loading drafts…</div>;

  const groups = data?.groups || [];
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold">Confirm catalog</h2>
        <SyncNow />
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Review draft classes from the index + auto-walk. Set the canonical script, year, origin, and LMS status,
        then confirm. Only confirmed classes feed the intelligence.
        {data?.orphan_file_count ? ` · ${data.orphan_file_count} orphan files` : ''}
      </p>
      <ContentSources />
      <EnrichmentReview />
      <CatalogHealth />
      <OfferedPricing />
      {groups.length === 0 && <div className="text-sm text-gray-500">No draft classes awaiting confirmation.</div>}
      {groups.map((g, i) => (
        <div key={i} className="mb-6">
          <div className="text-xs font-bold text-blue-600 mb-2">
            {g.program || 'Unassigned'} {g.year ? `· ${g.year}` : ''}
          </div>
          {g.classes.map((c) => <ClassCard key={c.id} cls={c} siblings={g.classes} />)}
        </div>
      ))}
    </div>
  );
}
